<script setup>
import ProductBox from '@/components/molecules/ProductBox.vue'
import { useProductStore } from '@/store/products'
import { useUserStore } from '@/store/user'
import { sendEvent } from '@/helpers/cerebro'
import { ref, computed, onMounted, watch } from 'vue'
import PaddlePaymentElement from '../molecules/PaddlePaymentElement.vue'
import SecureCheckIcon from '@/assets/icons/secure_check.svg'
import Modal from './Modal.vue'
import { changeEmail } from '@/services/authService'
import { useToast } from 'vue-toast-notification'
import 'vue-toast-notification/dist/theme-sugar.css'
import { useOnboardingStore } from '@/store/onboarding'
import { storeToRefs } from 'pinia'
import { unitAmountToPriceText } from '@/helpers/price'
import { sendGTagEvent } from '@/helpers/gtag'

const onboardingStore = useOnboardingStore()
const { discountRate, discountId, isSpecialOffer, isSpecialOfferActivated, discount } = storeToRefs(onboardingStore);
const {
  isDiscounted,
  theme,
} = defineProps(['isDiscounted', 'theme'])

const style = computed(() => ({
  '--background-color': theme === 'dark' ? 'var(--primary-darker)' : 'white', //background color
  '--product-box-background': theme === 'dark' ? 'var(--primary-darker)' : 'white', // product box background color
  '--product-box-border': theme === 'dark' ? 'white' : 'var(--primary-lighter)', // product box border color
  '--product-box-text-color': theme === 'dark' ? 'white' : 'var(--neutrals-900)', // product box text color
  '--product-box-secondary-text-color': theme === 'dark' ? 'var(--neutrals-400)' : 'var(--neutrals-400)', // product box secondary text color
  '--selected-product-background': theme === 'dark' ? 'white' : 'var(--primary-lighter)', // selected product background color
  '--selected-product-text-color': theme === 'dark' ? 'var(--neutrals-900)' : 'var(--neutrals-900)', // selected product text color
  '--selected-product-border-color': theme === 'dark' ? 'var(--primary-light)' : 'var(--primary-base)', // selected product border color
  '--most-popular-text-color': theme === 'dark' ? 'var(--primary-darker)' : 'white', // tag text color
  '--most-popular-background-color': theme === 'dark' ? 'var(--primary-light)' : 'var(--primary-base)', // tag background color
  '--save-text-color': theme === 'dark' ? 'var(--neutrals-900)' : 'var(--primary-base)', // save text color
  '--save-text-background-color': theme === 'dark' ? 'white' : 'var(--primary-light)', // save text background color
  '--selected-save-text-color': theme === 'dark' ? 'white' : 'white', // save text color
  '--selected-save-text-background-color': theme === 'dark' ? 'var(--primary-base)' : 'var(--primary-base)', // save text background color

  '--primary-color': theme === 'dark' ? 'var(--primary-darker)' : 'white', //background color
  '--secondary-color': theme === 'dark' ? 'white' : 'var(--primary-lighter)', // selected product background color
  '--accent-color': theme === 'dark' ? 'var(--primary-lighter)' : 'var(--primary-darker)', //tag bg, border colors
  'accent-color-2': theme === 'dark' ? 'white' : 'var(--primary-darker)', //button color
  '--text-primary': theme === 'dark' ? 'white' : 'var(--neutrals-900)', // main text color
  '--text-secondary': theme === 'dark' ? 'var(--neutrals-900)' : 'white', //tag text color
  '--text-dark-primary': 'var(--neutrals-900)', // for always dark colors
  '--text-light-primary': 'white', // for always light colors,
  marginTop: (theme === 'light') & isDiscounted ? '75px' : ''
}))

const toast = useToast()
const userStore = useUserStore()
const isCheckout = computed(() => {
  return userStore.getIsCheckout() && theme === 'light'
})

const isSpecialOfferModalOpened = ref(false)
const status = ref('idle')
const selectedIndex= computed(() => {
  return userStore.getSelectedProductIndex()
})

const paywallStructure = computed(() =>
  JSON.parse(userStore.getParameter('paywall_structure') || '{}')
)

const isPro = computed(() => userStore.isUserPro())
const productStore = useProductStore()
const remountKey = ref(0)

// make source dynamic for different sources
const source = computed(() => (isDiscounted ? 'special-offer-1' : 'default'))
const products = computed(() => {
  return productStore.getProductsWithSource(source.value)
})

const initiateAndRedirectToCheckout = async (product) => {
  if (!product ||status.value !== 'idle') return
  status.value = 'initiating'

  sendEvent({
    eventName: 'purchase_started',
    properties: {
      product_id: product.id,
      default_product: products.value[1].id,
      product_price: `$${((product.unit_amount / 100) * (1 - discountRate.value)).toFixed(2)}`,
      currency: product.currency ?? 'USD',
      discount_rate: discountRate.value,
      layout: 'classic_layout',
      source: source.value
    }
  })

  status.value = 'idle'
  sendEvent({ eventName: 'checkout', properties: { source: source.value } })
}

onMounted(() => {
  setTimeout(() => {
    selectProduct(1)    
  }, 50);
})

watch(
  () => userStore.getIsCheckout(),
  (newValue, oldValue) => {
    if(!oldValue && newValue && theme === 'dark') {
      initiateAndRedirectToCheckout(products.value[selectedIndex.value]);
    }
  },
  { deep: true }
)


watch(
  () => {
    source.value, products.value
  },
  () => {},
  { deep: true }
)

const selectProduct = (index) => {
  const product = products.value[index]
  if (!product) return
  sendEvent({
    eventName: 'select_plan',
    properties: {
      product_id: product.id,
      source: source.value,
      billing_cycle:
        (product.recurring && product.recurring.interval_count) +
        '_' +
        (product.recurring && product.recurring.interval),
      price: `$${(product.unit_amount / 100).toFixed(2)}`
    }
  })
  userStore.setSelectedProductIndex(index)
  sendGTagEvent({
    event: "view_item",
    product_id: product.id,
    product_price: discountedPrice.value,
    currency: product.currency ?? "USD",
    product_name: product.nickname,
  });
}

const selectedProduct = computed(() => {
  return products.value[selectedIndex.value]
})

const selectedProductPriceText = computed(() => {
  return unitAmountToPriceText(selectedProduct?.value?.unit_amount)
})

const checkoutParams = computed(() => {
  const product = selectedProduct.value
  if (!product) return
  return {
    product,
    price: product.unit_amount,
    interval: product.recurring && product.recurring.interval,
    intervalCount: product.recurring && product.recurring.interval_count,
    offersFreeTrial: product.offersFreeTrial,
    productId: product.id,
    source: source.value,
    isDiscounted: isDiscounted,
    discountId: discountId.value,
    isMostPopular: selectedIndex.value === 1,
    discountRate: discountRate.value
  }
})

const emailMismatch = computed(() => userStore.getEmailMismatch())
const correctEmail = ref(userStore.userData.email ?? '')
const isCorrectEmailValid = ref(userStore.userData.email ? true : false)

const discountedPrice = computed(() => {
  if (!selectedProduct.value) return
  return (
    (selectedProduct.value?.unit_amount *
      (isDiscounted ? 1 - discountRate.value : 1)) /
    100
  ).toFixed(2)
})

watch(correctEmail, (newVal) => {
  const emailRegex = /^\w+@[a-zA-Z_]+?\.[a-zA-Z]{2,3}$/
  if (emailRegex.test(newVal)) {
    isCorrectEmailValid.value = true
  } else {
    isCorrectEmailValid.value = false
  }
})

const submitNewEmail = async () => {
  if (!isCorrectEmailValid.value) return
  if (correctEmail.value === userStore.userData.email) {
    toast.success('Email updated successfully', { position: 'top-right' })
    userStore.setEmailMismatch(false)
    return
  }
  try {
    const resp = await changeEmail(correctEmail.value)
    if (resp.success) {
      if (resp.redirectLink) userStore.setRedirectLink(resp.redirectLink)
      toast.success('Email updated successfully', { position: 'top-right' })
    } else {
      toast.error('There is a already registered account with this email', {
        position: 'top-right'
      })
    }
    userStore.setEmailMismatch(false)
  } catch (error) {
    toast.error('Something went wrong, please try again later', {
      position: 'top-right'
    })
    userStore.setEmailMismatch(false)
  }
}

watch(
  () => userStore.userData.email,
  (newEmail, oldEmail) => {
    if (newEmail !== oldEmail) {
      remountKey.value++
      Paddle.Checkout.updateCheckout({
        customer: {
          email: newEmail
        }
      })
    }
  }
)

const onCloseCheckout = () => {
  userStore.setIsCheckout(false)
  if (isSpecialOffer.value && !isSpecialOfferActivated.value) {
    setTimeout(() => {
      isSpecialOfferModalOpened.value = true;
    }, 300);
  }
}

const claimSpecialDiscount = async () => {
  await onboardingStore.claimSpecialDiscount();
  isSpecialOfferModalOpened.value = false;
};

</script>

<template>
  <div class="container" :style="style">
    <p class="heading-5 center-text">Unlock Unlimited Access</p>
    <div v-if="!isPro && products && products.length > 0" class="products mt-16">
      <ProductBox
        v-for="(product, index) in products"
        :key="product.id"
        :product="product"
        :onboardingStyle="true"
        :isSelected="index === selectedIndex"
        class="each-product"
        :selectProduct="() => selectProduct(index)"
        :isMostPopular="index === 1"
        :discountAmount="discountRate"
        :isDiscounted="isDiscounted"
      />
      <button
        @click="() => userStore.setIsCheckout(true)"
        class="btn btn-primary label-md"
        style="height: 64px;"
        :class="`${theme === 'dark' ? 'dark' : ''} `"
      >
        Continue
      </button>
      <div class="payment-methods">
        <div class="secure-badge"><SecureCheckIcon /> &nbsp;Pay safe & secure</div>
        <img src="@/assets/images/payment_methods.png" alt="payment-methods" width="100%" />
      </div>
      <div class="subscription-info" v-if="isDiscounted">
        <p class="p-xs" v-if="discount?.recur">
          We’ve automatically applied discount to your first subscription price. Please note that
          your subscription will be automatically renewed at discounted price of ${{ discountedPrice }}
          at the end of chosen subscription term.
        </p>
        <p class="p-xs" v-else>
          We’ve automatically applied discount to your first subscription price. Please note that
          your subscription will be automatically renewed at {{ selectedProductPriceText }}
          at the end of chosen subscription term.
        </p>
      </div>
    </div>
    <div>
      <Modal :isVisible="isCheckout" @close="onCloseCheckout">
        <PaddlePaymentElement :key="remountKey" v-bind="checkoutParams" />
      </Modal>
      <Modal :isVisible="emailMismatch == true ?? false" :closeIcon="false">
        <div class="email-mismatch">
          <h2 class="heading-5">Email Mismatch</h2>
          <p class="p-md">
            The email doesn't match. <br />
            Please enter the correct email:
          </p>
          <input
            type="email"
            placeholder="Enter your correct email"
            class="p-md email-input"
            v-model="correctEmail"
          />
          <button class="btn btn-primary" :disabled="!isCorrectEmailValid" @click="submitNewEmail">
            Submit
          </button>
        </div>
      </Modal>
      <Modal :isVisible="isSpecialOfferModalOpened" @close="isSpecialOfferModalOpened = false" :closeIcon="false" :closeOnClickOutside="false">
        <div class="special-offer-modal">
          <img src="@/assets/images/special-discount.png" alt="Special Discount" class="special-offer-modal-image" />
          <h2>Special discount unlocked!</h2>
          <p class="p-md">
            We want you to reach your goals, so we’re offering an exclusive extra discount on your learning journey. Let’s get started!
          </p>
          <button class="btn btn-primary" @click="claimSpecialDiscount">
            Claim Discount
          </button>
        </div>
      </Modal>
    </div>
  </div>
</template>

<style scoped>
.btn-primary {
  border-radius: 40px;
}

.container {
  color: var(--text-primary);
  border-radius: 16px;
  background-color: var(--primary-color);
  padding-top: 24px;
  padding-bottom: 24px;
}

.products {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
}

.payment-methods {
  text-align: center;
  padding-bottom: 12px;
}

.secure-badge {
  display: inline-flex;
  justify-content: center;
  align-items: center;
  color: #4aa181;
  border: 1px solid #4aa181;
  padding: 6px 8px;
  border-radius: 8px;
  margin: 16px 0px 12px 0px;
  background: #00bb770d;
}

.subscription-info {
  text-align: center;
  padding: 8px 16px;
  color: var(--text-dark-primary);
  border: 1px solid var(--primary-color);
  background: var(--secondary-color);
  opacity: 0.5;
  border-radius: 12px;
}

.dark {
  background: var(--neutrals-0, #fff);
  color: var(--primary-color);
}

.dark:hover {
  background: var(--primary-light);
}

.email-mismatch {
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding: 40px 20px;
  width: 100%;
  max-width: 480px;
}

.email-mismatch > h2 {
  min-width: 340px;
}

.email-input {
  padding: 0px 10px;
  height: 55px;
  border: 1px solid var(--neutrals-200);
  width: 100%;
}

.special-offer-modal {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  text-align: center;
  gap: 12px;
  padding: 16px;
  width: 100%;
  max-width: 480px;
}

.special-offer-modal > h2 {
  color: var(--primary-base);
  font-size: 40px;
  font-weight: 600;
}

.special-offer-modal > p {
  color: var(--neutrals-500);
  font-size: 16px;
  font-weight: 400;
}

.special-offer-modal > button {
  margin-top: 12px;
  height: 60px;
}

.special-offer-modal-image {
  width: auto;
  height: 250px;
  object-fit: cover;
}
</style>
